import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card, Row, Col, Typography, Table, Tabs, Form, Input,
  Button, message, Space, Tag, Spin, Divider, Upload, Select,
} from "antd";
import {
  SaveOutlined, EditOutlined, UploadOutlined, DownloadOutlined, SoundOutlined,
} from "@ant-design/icons";
import { fetchCifAdminCountries, fetchCifAdminSections, updateCifAdminSection, uploadFlag, downloadFlag, updateCountryRegion, uploadAnthem, downloadAnthem } from "../../utils/cifAdminApi";
import { getAnthemUrl } from "../../utils/cifApi";
import { getFlagUrl } from "../../utils/countriesApi";
import CifFieldManager from "./CifFieldManager";
import "./CifAdmin.scss";

const { Text } = Typography;
const { TextArea } = Input;

const OIC_REGIONS = ["Africa", "Asia", "Europe", "Americas"];

function humanLabel(key) {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const SECTIONS = [
  { key: "background",  label: "Background",  color: "#123A78" },
  { key: "geography",   label: "Geography & Climate", color: "#1A4A8C" },
  { key: "demography",  label: "Society & Demographics", color: "#2260A8" },
  { key: "economy",     label: "Economy & Sectors", color: "#2E6DB4" },
  { key: "politics",    label: "Politics", color: "#123A78" },
  { key: "partnership", label: "Partnership", color: "#1A4A8C" },
  { key: "logistics",   label: "Logistics", color: "#2260A8" },
  { key: "links",       label: "Special Links", color: "#2E6DB4" },
];

const HIDDEN_FIELDS = new Set(["cid"]);
const SectionForm = React.memo(function SectionForm({ sectionKey, data, onSave, saving }) {
  const [form] = Form.useForm();
  const loading = data === undefined;

  useEffect(() => {
    if (data) {
      const values = { ...data };
      HIDDEN_FIELDS.forEach((f) => delete values[f]);
      form.setFieldsValue(values);
    } else {
      form.resetFields();
    }
  }, [data, form]);

  const fields = useMemo(
    () => (data ? Object.keys(data).filter((k) => !HIDDEN_FIELDS.has(k)) : []),
    [data],
  );

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const cleaned = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          cleaned[k] = v;
        }
      });
      await onSave(cleaned);
    } catch {
      // validation failed
    }
  }, [form, onSave]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          No data yet for this section. Click "Create & Edit" to initialize it.
        </Text>
        <Button
          type="dashed"
          icon={<SaveOutlined />}
          onClick={() => onSave({})}
          loading={saving}
        >
          Create & Edit {SECTIONS.find((s) => s.key === sectionKey)?.label || sectionKey}
        </Button>
      </div>
    );
  }

  return (
    <Form form={form} layout="vertical" size="small" style={{ marginTop: 8 }}>
      <Row gutter={[16, 0]}>
        {fields.map((field) => {
          const val = data[field];
          const isLong =
            (typeof val === "string" && val.length > 80) ||
            ["overview", "background", "description", "note", "issues", "structure", "selection",
             "branch", "court", "partners", "resources", "products", "opportunities", "possibilities",
             "organizations", "agencies", "candidacies", "institutions", "trainings", "workshop",
             "cooperation", "relationship", "visa", "places_to_visit", "historical", "sociological",
             "political_overview", "economy_overview", "cabinet", "voting_system", "mou",
            ].some((kw) => field.includes(kw));

          return (
            <Col xs={24} md={isLong ? 24 : 12} key={field}>
              <Form.Item
                name={field}
                label={<Text style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>{humanLabel(field)}</Text>}
              >
                {isLong ? (
                  <TextArea rows={3} style={{ fontSize: 13 }} />
                ) : (
                  <Input style={{ fontSize: 13 }} />
                )}
              </Form.Item>
            </Col>
          );
        })}
      </Row>
      <Divider style={{ margin: "12px 0" }} />
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
      >
        Save {SECTIONS.find((s) => s.key === sectionKey)?.label || sectionKey}
      </Button>
    </Form>
  );
});

export default function CifAdmin() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCcode, setSelectedCcode] = useState(null);
  const [sectionsData, setSectionsData] = useState(null);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [activeTab, setActiveTab] = useState("background");
  const [fieldConfigRefresh, setFieldConfigRefresh] = useState(0);
  const [adminLang, setAdminLang] = useState("en");

  useEffect(() => {
    setLoading(true);
    fetchCifAdminCountries(adminLang)
      .then(setCountries)
      .catch(() => message.error("Failed to load countries"))
      .finally(() => setLoading(false));
  }, [adminLang]);

  useEffect(() => {
    if (!selectedCcode) {
      setSectionsData(null);
      return;
    }
    setSectionsLoading(true);
    setActiveTab("background");
    fetchCifAdminSections(selectedCcode, adminLang)
      .then(setSectionsData)
      .catch(() => message.error("Failed to load CIF data"))
      .finally(() => setSectionsLoading(false));
  }, [selectedCcode, fieldConfigRefresh, adminLang]);

  const handleSave = useCallback(async (section, values) => {
    setSavingSection(section);
    try {
      await updateCifAdminSection(selectedCcode, section, values, adminLang);
      message.success(`${SECTIONS.find((s) => s.key === section)?.label} saved`);
      const updated = await fetchCifAdminSections(selectedCcode, adminLang);
      setSectionsData(updated);
    } catch {
      message.error("Failed to save");
    } finally {
      setSavingSection(null);
    }
  }, [selectedCcode, adminLang]);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.c_code === selectedCcode),
    [countries, selectedCcode],
  );

  const columns = useMemo(() => [
    {
      title: "Country",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name?.localeCompare(b.name),
      defaultSortOrder: "ascend",
      render: (name, record) => (
          <Text strong={selectedCcode === record.c_code}>{name}</Text>
      ),
    },
  ], [selectedCcode]);

  const tabItems = useMemo(() => [
    ...SECTIONS.map((sec) => ({
      key: sec.key,
      label: (
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          {sec.label}
        </span>
      ),
      children: (
        <SectionForm
          sectionKey={sec.key}
          data={sectionsLoading ? undefined : sectionsData?.[sec.key]}
          onSave={(values) => handleSave(sec.key, values)}
          saving={savingSection === sec.key}
        />
      ),
    })),
  ], [sectionsLoading, sectionsData, handleSave, savingSection]);

  const handleFlagUpload = useCallback(async (file) => {
    if (!selectedCcode) return false;
    try {
      await uploadFlag(selectedCcode, file);
      message.success("Flag uploaded successfully");
    } catch {
      message.error("Failed to upload flag");
    }
    return false;
  }, [selectedCcode]);

  const handleRegionChange = useCallback(async (newRegion) => {
    if (!selectedCcode) return;
    try {
      await updateCountryRegion(selectedCcode, newRegion);
      message.success("Region updated");
      setCountries((prev) =>
        prev.map((c) =>
          c.c_code === selectedCcode ? { ...c, region: newRegion } : c
        )
      );
    } catch {
      message.error("Failed to update region");
    }
  }, [selectedCcode]);

  function FlagManager() {
    const [flagKey, setFlagKey] = useState(0);
    if (!selectedCcode || !selectedCountry) return null;
    return (
      <Card
        size="small"
        style={{ background: "#fafafa" }}
        styles={{ body: { padding: "12px 16px" } }}
      >
        <Row gutter={16} align="middle">
          <Col>
            <img
              key={flagKey}
              src={getFlagUrl(selectedCcode)}
              alt={selectedCountry.name}
              style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4, border: "1px solid #e8e8e8" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </Col>
          <Col flex="auto">
            <Text strong style={{ fontSize: 13 }}>Country Flag</Text>
            <div style={{ marginTop: 4 }}>
              <Upload
                accept=".svg,.png,.jpg,.jpeg,.gif,.webp"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleFlagUpload(file);
                  setFlagKey((k) => k + 1);
                  return false;
                }}
              >
                <Button size="small" icon={<UploadOutlined />} style={{ marginRight: 8 }}>
                  Upload Flag
                </Button>
              </Upload>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadFlag(selectedCcode)}
              >
                Download
              </Button>
            </div>
          </Col>
        </Row>
      </Card>
    );
  }

  function AnthemManager() {
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);
    const [anthemKey, setAnthemKey] = useState(0);

    useEffect(() => {
      if (!selectedCcode) return;
      audioRef.current = new Audio(getAnthemUrl(selectedCcode));
      audioRef.current.addEventListener("ended", () => setPlaying(false));
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }, [selectedCcode, anthemKey]);

    const handleAnthemUpload = useCallback(async (file) => {
      if (!selectedCcode) return false;
      try {
        await uploadAnthem(selectedCcode, file);
        message.success("Anthem uploaded successfully");
        setAnthemKey((k) => k + 1);
      } catch {
        message.error("Failed to upload anthem");
      }
      return false;
    }, [selectedCcode]);

    const togglePlay = () => {
      const a = audioRef.current;
      if (!a) return;
      if (playing) {
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
      } else {
        a.play().then(() => setPlaying(true)).catch(() => {});
      }
    };

    if (!selectedCcode || !selectedCountry) return null;
    return (
      <Card
        size="small"
        style={{ background: "#fafafa" }}
        styles={{ body: { padding: "12px 16px" } }}
      >
        <Row gutter={16} align="middle">
          <Col>
            <SoundOutlined style={{ fontSize: 24, color: "#1A4A8C" }} />
          </Col>
          <Col flex="auto">
            <Text strong style={{ fontSize: 13 }}>National Anthem</Text>
            <div style={{ marginTop: 4 }}>
              <Button
                size="small"
                icon={<SoundOutlined />}
                onClick={togglePlay}
                style={{ marginRight: 8 }}
              >
                {playing ? "Stop" : "Play"}
              </Button>
              <Upload
                accept=".mp3,.wav,.ogg,.aac,.m4a,.webm"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleAnthemUpload(file);
                  return false;
                }}
              >
                <Button size="small" icon={<UploadOutlined />} style={{ marginRight: 8 }}>
                  Upload
                </Button>
              </Upload>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadAnthem(selectedCcode)}
              >
                Download
              </Button>
            </div>
          </Col>
        </Row>
      </Card>
    );
  }

  function RegionManager() {
    const [region, setRegion] = useState(selectedCountry?.region || "");
    useEffect(() => {
      setRegion(selectedCountry?.region || "");
    }, [selectedCountry]);
    if (!selectedCcode || !selectedCountry) return null;
    return (
      <Card
        size="small"
        style={{ marginBottom: 16, background: "#fafafa" }}
        styles={{ body: { padding: "12px 16px" } }}
      >
        <Row gutter={16} align="middle">
          <Col>
            <Text strong style={{ fontSize: 13 }}>Region</Text>
          </Col>
          <Col flex="auto">
            <Select
              value={region || undefined}
              onChange={setRegion}
              placeholder="Select region"
              style={{ width: 200 }}
              options={OIC_REGIONS.map((r) => ({ value: r, label: r }))}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              size="small"
              onClick={() => handleRegionChange(region)}
              disabled={!region || region === selectedCountry?.region}
            >
              Save Region
            </Button>
          </Col>
        </Row>
      </Card>
    );
  }

  return (
    <div className="cif-admin">
      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <Space>
                <span>OIC Member States</span>
                <Tag>{countries.length} countries</Tag>
              </Space>
            }
            className="cif-admin__list-card"
          >
            <Table
              dataSource={countries}
              columns={columns}
              rowKey="c_code"
              loading={loading}
              size="small"
              pagination={false}
              scroll={{ y: "80vh" }}
              showHeader={false}
              rowClassName={(record) =>
                selectedCcode === record.c_code ? "ant-table-row--selected" : ""
              }
              onRow={(record) => ({
                onClick: () => setSelectedCcode(record.c_code),
                style: { cursor: "pointer" },
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            className="cif-admin__editor-card"
            title={
              selectedCountry ? (
                <Space>
                  <EditOutlined />
                  <Text strong>{selectedCountry.name}</Text>
                </Space>
              ) : (
                <Text type="secondary">Select a country to edit</Text>
              )
            }
          >
            {selectedCcode ? (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <FlagManager />
                  </Col>
                  <Col xs={24} sm={12}>
                    <AnthemManager />
                  </Col>
                </Row>
                <RegionManager />
              </>
            ) : null}
            <div className="cif-admin__lang-bar">
              <Text strong style={{ fontSize: 13, color: "#555" }}>Language</Text>
              <Space>
                {[
                  { key: "en", label: "English" },
                  { key: "fr", label: "Français" },
                  { key: "ar", label: "العربية" },
                ].map((l) => (
                  <Button
                    key={l.key}
                    size="small"
                    type={adminLang === l.key ? "primary" : "default"}
                    shape="round"
                    onClick={() => setAdminLang(l.key)}
                  >
                    {l.label}
                  </Button>
                ))}
              </Space>
              {selectedCountry && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Editing: {selectedCountry.name}
                </Text>
              )}
            </div>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              size="small"
              tabPosition="top"
              style={{ minHeight: 400 }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card
            title={
              <Space>
                <span>Field Configuration</span>
              </Space>
            }
            className="cif-admin__field-config-card"
          >
            <CifFieldManager onFieldConfigChange={() => setFieldConfigRefresh((c) => c + 1)} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
